import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
} from "@coreui/react";
import PublicHeader from "../components/public/PublicHeader";
import PublicFooter from "../components/public/PublicFooter";
import "../styles/public-experience.css";

const heroTrustPoints = [
  "Strukturierte Qualifikationslogik",
  "Sprach- und Wissenstests",
  "Score-basierte Passung",
  "Vorbereitete Unterlagen für nächste Schritte",
];

const heroProducts = [
  {
    title: "Jobs",
    text: "Offene Stellen und Recruiting-Wege bleiben als nächster Schritt sichtbar.",
  },
  {
    title: "Qualifikationen",
    text: "Nachweise, Erfahrung und Abschlüsse werden geordnet lesbar gemacht.",
  },
  {
    title: "Sprachtests",
    text: "Sprachlogik und Zertifikate bleiben nachvollziehbar getrennt.",
  },
  {
    title: "Wissenstests",
    text: "Allgemeinwissen, Deutschlandwissen und Fachwissen ergänzen das Profil.",
  },
  {
    title: "Scoring und Matching",
    text: "Passung wird strukturiert vorbereitet statt nur grob eingeschätzt.",
  },
  {
    title: "Visa-Dokumente",
    text: "Unterlagen für weitere Schritte werden geordnet vorbereitet.",
  },
];

const productLines = [
  {
    id: "jobs",
    title: "Jobs",
    summary:
      "Experts4Europe verbindet offene Stellen mit klaren Anforderungen und dem jeweils nächsten Recruiting-Schritt. So bleibt der Einstieg nicht bei einer bloßen Anzeige stehen.",
  },
  {
    id: "qualifikationen",
    title: "Qualifikationen und Nachweise",
    summary:
      "Ausbildung, Berufserfahrung und Dokumente werden geordnet erfasst und lesbarer gemacht. Das schafft nachvollziehbare Profile statt loser Dateisammlungen.",
  },
  {
    id: "sprachtests",
    title: "Sprachtests und Sprachlogik",
    summary:
      "Interne Sprachtests und externe Nachweise werden sauber unterschieden und gemeinsam einordbar gemacht. Sprachstand wird damit klarer und nutzbarer sichtbar.",
  },
  {
    id: "wissenstests",
    title: "Wissenstests",
    summary:
      "Allgemeinwissen, Deutschlandwissen und fachbezogenes Wissen liefern zusätzliche Einordnung. Sichtbar werden Vergleichbarkeit, Stärken und Lernbedarfe.",
  },
  {
    id: "matching",
    title: "Scoring und Matching",
    summary:
      "Signale aus Profil, Sprache, Wissen und Nachweisen fließen in eine strukturiertere Passung ein. Das hilft bei klareren Entscheidungen auf beiden Seiten.",
  },
  {
    id: "visa-dokumente",
    title: "Visa- und Einwanderungsdokumente",
    summary:
      "Unterlagen für Visa- und Einwanderungswege werden vorbereitet, geordnet und verständlicher aufbereitet. Der Nutzen liegt in Struktur und Vorbereitung, nicht in falschen Behördenversprechen.",
  },
];

const roleCards = [
  {
    id: "fuer-bewerber",
    eyebrow: "Für Bewerber",
    title: "Profil, Qualifikationen und nächste Schritte an einem Ort",
    description:
      "Bewerber bauen ein klares Profil auf, ordnen Nachweise, zeigen Sprachstand und Wissen und bereiten Unterlagen für den weiteren Weg vor.",
    points: [
      "Profil und Zielbild aufbauen",
      "Qualifikationen und Nachweise erfassen",
      "Sprachstand und Wissen sichtbar machen",
      "Unterlagen für nächste Schritte vorbereiten",
      "Passende Jobs finden",
    ],
    ctaLabel: "Als Bewerber starten",
    href: "/register",
  },
  {
    id: "fuer-arbeitgeber",
    eyebrow: "Für Arbeitgeber",
    title: "Fachkräfte strukturierter finden und besser einordnen",
    description:
      "Arbeitgeber sehen nicht nur Dokumente, sondern besser lesbare Profile, freigegebene Signale und vorbereitete Entscheidungsgrundlagen für Recruiting-Wege in Europa.",
    points: [
      "Stellen besetzen",
      "Profile besser einordnen",
      "Strukturierte Signale erhalten",
      "Entscheidungen vorbereiten",
      "Recruiting-Wege klarer führen",
    ],
    ctaLabel: "Fachkräfte finden",
    href: "/register",
  },
];

const platformHighlights = [
  "Sprach- und Wissenstests ergänzen Qualifikationen und Nachweise.",
  "Score-basierte Passung bleibt öffentlich verständlich statt technisch abstrakt.",
  "Jobs und Bewerbungen werden entlang des nächsten Schritts geführt.",
  "Unterlagen werden erst für die nächsten Schritte vorbereitet.",
];

const processSteps = [
  {
    step: "01",
    title: "Profil und Ziel erfassen",
    description: "Der Einstieg beginnt mit Rolle, Zielbild und einem geordneten Profil statt mit vielen Einzelangaben.",
  },
  {
    step: "02",
    title: "Qualifikationen und Nachweise einordnen",
    description: "Ausbildung, Erfahrung und Dokumente werden lesbar zusammengeführt und vorbereitet.",
  },
  {
    step: "03",
    title: "Sprachstand und Wissen prüfen",
    description: "Sprachtests, Sprachlogik und Wissenstests ergänzen das Profil um weitere Orientierung.",
  },
  {
    step: "04",
    title: "Score und Passung sichtbar machen",
    description: "Relevante Signale werden so gebündelt, dass Stellen und Profile strukturierter zueinanderfinden.",
  },
  {
    step: "05",
    title: "Jobs und Bewerbungen strukturieren",
    description: "Recruiting-Wege bleiben nachvollziehbar statt in Anzeige, Dokument und Einzelschritt zu zerfallen.",
  },
  {
    step: "06",
    title: "Unterlagen für nächste Schritte vorbereiten",
    description: "Dokumente werden für Recruiting-, Visa- und Einwanderungswege geordnet vorbereitet.",
  },
];

const trustCards = [
  {
    title: "Struktur statt Dokumentenchaos",
    text: "Nachweise, Tests und vorbereitete Unterlagen werden geordnet statt lose zusammengetragen.",
  },
  {
    title: "Nachvollziehbare Einordnung",
    text: "Qualifikationen, Sprache, Wissen und Passung werden sichtbar verbunden statt chaotisch nebeneinandergestellt.",
  },
  {
    title: "Kontrollierte Freigaben",
    text: "Profile bleiben nachvollziehbar, ohne dass Rohdokumente pauschal in jede Richtung weitergereicht werden.",
  },
  {
    title: "Europaweiter Fokus",
    text: "Experts4Europe verbindet Recruiting, Qualifikationen und Einwanderungsvorbereitung in einem gemeinsamen Rahmen.",
  },
];

export default function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Experts4Europe | Jobs, Qualifikationen und nächste Schritte für Europa";
  }, []);

  return (
    <div className="public-core-page public-core-page--home">
      <section className="public-core-band public-core-band--hero">
        <PublicHeader mode="marketing" />

        <CContainer fluid className="public-homepage-hero-shell">
          <CRow className="public-homepage-hero-grid align-items-start justify-content-between">
            <CCol lg={6} xl={6} className="public-homepage-hero-col public-homepage-hero-col--copy">
              <div className="public-homepage-hero-copy">
                <span className="public-core-eyebrow">Experts4Europe Plattform</span>
                <h1>Jobs, Qualifikationen und nächste Schritte für Europa</h1>
                <p className="public-homepage-hero-lead">
                  Experts4Europe verbindet Jobs, Qualifikationen, Sprachstand, Wissenstests und vorbereitete
                  Unterlagen zu einem geordneten Weg für Recruiting und Einwanderung in Europa.
                </p>
                <div className="public-homepage-hero-actions">
                  <CButton
                    type="button"
                    className="public-core-btn public-core-btn--primary"
                    onClick={() => navigate("/register")}
                  >
                    Als Bewerber starten
                  </CButton>
                  <CButton
                    type="button"
                    className="public-core-btn public-core-btn--subtle"
                    onClick={() => navigate("/register")}
                  >
                    Fachkräfte finden
                  </CButton>
                </div>
                <div className="public-homepage-trust-strip">
                  {heroTrustPoints.map((point) => (
                    <span key={point}>{point}</span>
                  ))}
                </div>
              </div>
            </CCol>

            <CCol lg={6} xl={6} className="public-homepage-hero-col public-homepage-hero-col--panel">
              <CCard className="public-core-panel public-homepage-hero-panel">
                <CCardBody>
                  <span className="public-core-eyebrow public-core-eyebrow--soft">Produktlogik</span>
                  <div className="public-homepage-hero-panel__head">
                    <h2>Sechs Produktbereiche, klar geordnet</h2>
                    <span className="public-core-panel__badge">Mehr als eine Stellenbörse</span>
                  </div>
                  <p className="public-homepage-hero-panel__lead">
                    Jobs, Qualifikationen, Sprachtests, Wissenstests, Matching und vorbereitete Unterlagen
                    greifen sichtbar ineinander, ohne nach außen wie eine technische Innenwelt zu wirken.
                  </p>
                  <div className="public-homepage-hero-proof-grid">
                    {heroProducts.map((item) => (
                      <article key={item.title} className="public-homepage-hero-proof-item">
                        <strong>{item.title}</strong>
                        <p>{item.text}</p>
                      </article>
                    ))}
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--light public-homepage-featured-band" id="produkte">
        <CContainer fluid className="public-core-container public-homepage-shell public-homepage-shell--section">
          <div className="public-homepage-section-head public-homepage-section-head--center">
            <span className="public-core-eyebrow">Produkte</span>
            <h2>Sechs Bausteine für Recruiting, Einordnung und Vorbereitung</h2>
            <p>
              Die sechs Produktbereiche greifen als geordneter Weg für Recruiting, Einordnung und vorbereitete
              nächste Schritte in Europa ineinander.
            </p>
          </div>

          <CRow className="g-4">
            {productLines.map((item, index) => (
              <CCol xl={4} md={6} key={item.title}>
                <CCard id={item.id} className="public-core-card public-homepage-product-card">
                  <CCardBody>
                    <span className="public-homepage-product-index">0{index + 1}</span>
                    <span className="public-core-card-kicker">Produktbereich</span>
                    <h3>{item.title}</h3>
                    <p>{item.summary}</p>
                  </CCardBody>
                </CCard>
              </CCol>
            ))}
          </CRow>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--neutral">
        <CContainer fluid className="public-core-container public-homepage-shell public-homepage-shell--section">
          <CRow className="g-4 align-items-start">
            <CCol xl={4}>
              <div className="public-homepage-section-head">
                <span className="public-core-eyebrow">Für wen ist die Plattform?</span>
                <h2>Zwei klare Wege für Bewerber und Arbeitgeber</h2>
                <p>
                  Experts4Europe bündelt Jobs, Einordnung und vorbereitete nächste Schritte in einer
                  gemeinsamen öffentlichen Plattformlogik.
                </p>
              </div>
              <div className="public-homepage-aside-note">
                <strong>Mehr als ein Jobboard</strong>
                <p>
                  Die Plattform verbindet strukturierte Profile, Sprach- und Wissenstests, Score-basierte
                  Passung und geordnete Unterlagen für die nächsten Schritte.
                </p>
                <ul className="public-core-list">
                  {platformHighlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </CCol>
            <CCol xl={8}>
              <CRow className="g-4">
                {roleCards.map((card) => (
                  <CCol md={6} key={card.id} id={card.id}>
                    <CCard className="public-core-card public-homepage-audience-card">
                      <CCardBody>
                        <span className="public-core-eyebrow">{card.eyebrow}</span>
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                        <ul className="public-core-list">
                          {card.points.map((point) => (
                            <li key={point}>{point}</li>
                          ))}
                        </ul>
                        <CButton
                          type="button"
                          className="public-core-btn public-core-btn--light"
                          onClick={() => navigate(card.href)}
                        >
                          {card.ctaLabel}
                        </CButton>
                      </CCardBody>
                    </CCard>
                  </CCol>
                ))}
              </CRow>
            </CCol>
          </CRow>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--light" id="ablauf">
        <CContainer fluid className="public-core-container public-homepage-shell public-homepage-shell--section">
          <CRow className="g-4 align-items-start">
            <CCol xl={4}>
              <div className="public-homepage-section-head">
                <span className="public-core-eyebrow">So funktioniert es</span>
                <h2>Vom Profil bis zu vorbereiteten Unterlagen</h2>
                <p>
                  Die Homepage folgt einer klaren Schrittlogik: erst Einordnung, dann Passung, dann der
                  vorbereitete nächste Schritt.
                </p>
              </div>
              <div className="public-homepage-aside-note">
                <strong>Was in jeder Phase sichtbar bleibt</strong>
                <ul className="public-core-list">
                  {platformHighlights.slice(0, 3).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </CCol>
            <CCol xl={8}>
              <div className="public-homepage-steps-grid">
                {processSteps.map((step) => (
                  <article key={step.step} className="public-homepage-step-card">
                    <span className="public-homepage-step-card__index">{step.step}</span>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </CCol>
          </CRow>
        </CContainer>
      </section>

      <section className="public-core-band public-core-band--neutral" id="vertrauen">
        <CContainer fluid className="public-core-container public-homepage-shell public-homepage-shell--section">
          <CRow className="g-4 align-items-center">
            <CCol xl={5}>
              <div className="public-homepage-section-head">
                <span className="public-core-eyebrow">Plattform und Vertrauen</span>
                <h2>Mehr Struktur, weniger Dokumentenchaos</h2>
                <p>
                  Experts4Europe kommuniziert öffentlich über geordnete Profile, nachvollziehbare Einordnung
                  und kontrollierte Freigaben statt über interne Kontrollräume.
                </p>
              </div>
            </CCol>
            <CCol xl={7}>
              <CRow className="g-4">
                {trustCards.map((item) => (
                  <CCol md={6} key={item.title}>
                    <CCard className="public-core-card public-homepage-trust-card">
                      <CCardBody>
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </CCardBody>
                    </CCard>
                  </CCol>
                ))}
              </CRow>
            </CCol>
          </CRow>

          <CCard className="public-core-card public-homepage-cta-band">
            <CCardBody>
              <CRow className="g-4 align-items-center">
                <CCol xl={7}>
                  <span className="public-core-eyebrow">Nächster Schritt</span>
                  <h2>Die Plattform dort nutzen, wo Jobs, Einordnung und Vorbereitung zusammenkommen</h2>
                  <p>
                    Bewerber können ihren Weg nach Europa geordnet vorbereiten. Arbeitgeber können Fachkräfte
                    besser einordnen und Recruiting-Wege strukturierter führen.
                  </p>
                </CCol>
                <CCol xl={5}>
                  <div className="public-homepage-cta-actions">
                    <CButton
                      type="button"
                      className="public-core-btn public-core-btn--primary"
                      onClick={() => navigate("/register")}
                    >
                      Bewerberzugang
                    </CButton>
                    <CButton
                      type="button"
                      className="public-core-btn public-core-btn--light"
                      onClick={() => navigate("/register")}
                    >
                      Arbeitgeberzugang
                    </CButton>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CContainer>
      </section>

      <PublicFooter />
    </div>
  );
}
